<template>
  <section
    :class="[
      'base-wrapper',
      `text-${textAlign}`,
      backgroundColor,
      ...handleMediaPosition,
      {'has-footer': slots.actions}
    ]"
  >
    <div class="base-wrapper--inside-content">
      <div v-if="slots.media" class="base-wrapper__media">
        <slot name="media"></slot>
      </div>

      <div v-if="heading || subline" :class="['base-wrapper__header', {'base-wrapper__header--has-colorsplat': !!colorSplat && (!slots.default || slots.default.length === 0 ) } ]">
        <img v-if="colorSplat" :src="imageUrl" alt="colorsplat" class="colorsplat" />
        <component :is="headingTag" v-if="headingTag" v-clean-html="heading" :class="headingClass" />
        <component :is="sublineTag" v-if="sublineTag" v-clean-html="subline" :class="sublineClass" class="base-wrapper__subline" />
      </div>

      <div v-if="slots.default" class="base-wrapper__content">
        <slot name="default"></slot>
      </div>
    </div>

    <div v-if="slots.actions" class="base-wrapper__footer">
      <slot name="actions"></slot>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { Heading, HeadlineTag, Subline } from '../../../types/heading'
import { CompanyColor } from '../../../types/color'
import Media from '../Media'
import { ElementAlignment, TextAlignment } from '../../../types/alignment'
import { VerticalPosition } from '../../../types/position'

export interface BaseComponentTemplate extends Heading, Subline {
  headingTag?: HeadlineTag
  headingClass?: HeadlineTag
  sublineTag?: HeadlineTag
  sublineClass?: HeadlineTag
  textAlign?: TextAlignment
  actionsAlignment?: ElementAlignment
  backgroundColor?: CompanyColor
  mediaPosition?: VerticalPosition
  colorSplat?: boolean
  imageUrl?: string
}

const {
  heading = '',
  headingTag = 'h1',
  headingClass = '',
  subline = '',
  sublineTag = '',
  sublineClass = '',
  textAlign = 'left',
  actionsAlignment = 'flex-end',
  backgroundColor = '',
  mediaPosition = 'top',
  colorSplat = false,
  imageUrl = 'https://a.storyblok.com/f/167189/560x208/cd81717fc3/brushcoral.webp/m/',
} = defineProps<BaseComponentTemplate>()

const slots = defineSlots<{
  default?(): void
  media?(): (typeof Media)[]
  actions?(): unknown[]
}>()

const handleMediaPosition = computed(() => {
  const hasMedia = slots.media && slots.media().length > 0
  return hasMedia ? [`base-wrapper__media--${mediaPosition}`] : ''
})

</script>

<style lang='scss' scoped>
@use '../../../styles' as index;

.base-wrapper {
  display: grid;
  grid-template: "content" auto / auto;
  flex: 1 1 0;
  margin-block: 1rem;
  gap: 1.5rem;
  max-width: var(--maxContainerWidth);
  overflow: hidden;

  &.has-footer {
    grid-template-rows: auto 1fr;
    grid-template-areas:
    "content" "footer";
  }

  &--inside-content {
    grid-area: content;
    display: flex;
    flex-flow: column;
    gap: 1.5rem;
    overflow: hidden;
  }

  &__media {
    flex-direction: column;

    & :slotted(.slider-container) {
      justify-content: center;
      width: 100%;
      max-height: 23rem;
      margin-bottom: 1.5rem;
      vertical-align: top;
    }

    &--bottom {
      & :slotted(.slider-container) {
        order: 1;
      }
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    position: relative;
    z-index: 1;

    &--has-colorsplat {
      margin-top: 0.6rem;
      min-height: 5rem;
    }

    .colorsplat {
      position: absolute;
      top: 0;
      left: 0;
      width: 17.5rem;
      height: 6.5rem;
      z-index: -1;
      transform: translateY(-10px);

      .text-right & {
        left: auto;
        right: 0;
      }

      .text-center & {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }

  &__footer {
    grid-area: footer;
    display: flex;
    flex-direction: column;
    gap: 1.125rem;
    align-self: end;
    align-items: v-bind('actionsAlignment');

    & :slotted(.base-button) {
      width: fit-content;
    }
  }

  :is(&__content, &__header, &__media, &__footer, &--inside-content) {
    width: 100%;
  }

  @each $color in index.$backgroundColors {
    &.#{$color} {
      padding-block: 1rem;

      :is(.base-wrapper__footer, .base-wrapper--inside-content) {
        max-width: var(--maxContainerChildWidth);
        margin-inline: auto;
        padding-inline: 1rem;
      }
    }
  }
}
</style>
